import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { GridLegacy as Grid } from "@mui/material";
import GroupIcon from "../../Assets/images/svg/Icons/Group 578.svg";
import { DeleteApiMethod, GetApiMethod } from "../../Utils/ApiService";
import "../../Components/Common/Styles/Formstyles.css";
import SuccessDialog, { useSuccessDialog } from "../../Components/Common/SuccesMessages/SuccessDialog";

interface ServiceEntry {
  service: string;
  cost: string;
  id?: string;
}

interface ServicesFormProps {
  formData: {
    providerId?: string;
    services: ServiceEntry[];
  };
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  handleNext: () => void;
  handleBack: () => void;
}

interface ServiceOption {
  id: string;
  name: string;
  type: string;
}

const ServicesForm: React.FC<ServicesFormProps> = ({
  formData,
  setFormData,
  handleNext,
  handleBack,
}) => {
  const [services, setServices] = useState<ServiceEntry[]>([{ service: "", cost: "" }]);
  const hasFetched = useRef(false);
  const [serviceOptions, setServiceOptions] = useState<ServiceOption[]>([]);
  const { open: successOpen, message, showSuccess, closeSuccess } = useSuccessDialog();

  // 🔹 Fetch master services
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await GetApiMethod("service/list");
        const allRecords = response?.data?.data?.records ?? [];
        const servicesOnly = allRecords.filter((item: any) => item.type === "Services");
        setServiceOptions(servicesOnly);
      } catch (error) {
        console.error("Error fetching services:", error);
        setServiceOptions([]);
      }
    };
    fetchServices();
  }, []);

  // 🔹 Prefill from parent formData when editing
  useEffect(() => {
    if (formData.services && formData.services.length > 0) {
      setServices([...formData.services]);
    }
  }, [formData.services]);

  // 🔹 Fetch provider's services if editing (to ensure fresh data)
  useEffect(() => {
    if (!formData.providerId || hasFetched.current || serviceOptions.length === 0) return;

    const fetchProviderData = async () => {
      try {
        const res = await GetApiMethod(`provider/${formData.providerId}`);
        const providerData = res?.data?.data;

        const savedServices =
          providerData?.providerServices
            ?.filter((s: any) => s.type === "Services")
            ?.map((s: any) => ({
              id: s.id,
              service: s.serviceId,
              cost: String(s.cost),
            })) ?? [];

        setServices(savedServices.length ? savedServices : [{ service: "", cost: "" }]);
        setFormData((prev: any) => ({
          ...prev,
          services: savedServices,
        }));

        hasFetched.current = true;
      } catch (err) {
        console.error("Error fetching provider services:", err);
      }
    };

    fetchProviderData();
  }, [formData.providerId, serviceOptions]);

  // 🔹 Input handlers
  const handleServiceChange = (index: number, field: keyof ServiceEntry, value: string) => {
    const updated = [...services];
    updated[index][field] = value;
    setServices(updated);
    setFormData((prev: any) => ({ ...prev, services: updated }));
  };

  const addService = () => {
    const updated = [...services, { service: "", cost: "" }];
    setServices(updated);
    setFormData((prev: any) => ({ ...prev, services: updated }));
  };

const removeService = async (index: number) => {
  const target = services[index];

  // If the service has an id → delete from backend first
  if (target?.id) {
    try {
      await DeleteApiMethod(`provider-service/delete/${target.id}`);
      showSuccess("Service deleted successfully!");
    } catch (err) {
      console.error("Error deleting service:", err);
      showSuccess("Failed to delete service. Please try again.");
      return;
    }
  }

  // Remove from UI and form state
  const updated = services.filter((_, i) => i !== index);
  setServices(updated);
  setFormData((prev: any) => ({ ...prev, services: updated }));
};


  // 🔹 Validation before next step
  const handleNextClick = () => {
    const valid = services.every((s) => s.service && s.cost);
    if (!valid) {
      showSuccess("Please fill all service and cost fields.");
      return;
    }

    // showSuccess("Services validated successfully!");
    setTimeout(() => {
      closeSuccess();
      handleNext();
    }, 800);
  };

  return (
    <Box>
      {services.map((entry, index) => (
        <Box key={index} mb={3}>
          <Grid container spacing={3} alignItems="flex-end">
            <Grid item xs={12} md={4}>
              <Typography className="form-input">Services</Typography>
              <Select
                className="form-input"
                value={entry.service}
                onChange={(e) => handleServiceChange(index, "service", e.target.value)}
                fullWidth
                variant="standard"
                displayEmpty
              >
                <MenuItem value="">Choose</MenuItem>
                {serviceOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography className="form-input">Cost Per Hour</Typography>
              <TextField
                className="form-input"
                value={entry.cost}
                onChange={(e) => handleServiceChange(index, "cost", e.target.value)}
                fullWidth
                variant="standard"
                placeholder="₹ | Type here"
              />
            </Grid>

            <Grid item xs={12} md={3} display="flex" alignItems="center">
              {index === 0 ? (
                <Button onClick={addService} className="Add-new">
                  + Add More Services
                </Button>
              ) : (
                <IconButton
                  onClick={() => removeService(index)}
                  color="error"
                  size="small"
                  sx={{ mt: 2 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Grid>
          </Grid>
        </Box>
      ))}

      <Box display="flex" justifyContent="start" mt={2}>
        <Button onClick={handleBack} className="btn-back">
          <Box component="img" src={GroupIcon} sx={{ mr: 1 }} />
          Back
        </Button>

        <Button onClick={handleNextClick} className="btn-next" sx={{ ml: 2 }}>
          Next
        </Button>
      </Box>

      <SuccessDialog open={successOpen} onClose={closeSuccess} message={message} />
    </Box>
  );
};

export default ServicesForm;
